/**
 * UnifiedDashboard - Main Container Component
 *
 * This is the primary dashboard view that consolidates all patient/appointment
 * data into a single, unified interface. It includes:
 * - Two main tabs: Overview (Waiting Room + Queue Info) and Dashboard (Patient Table)
 * - Auto-refresh functionality (non-blocking background refresh)
 *
 * Data Fetching Strategy:
 * - All 4 APIs fetched in parallel via Promise.all
 * - Initial load shows skeleton/empty state, not blocking spinner
 * - Background refresh doesn't block UI interaction
 */

import { useMemo, useState } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  Typography,
  Badge,
  Tooltip,
  Spin,
} from 'antd';
import {
  ArrowUpOutlined,
  ReloadOutlined,
  SyncOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useUnifiedDashboardStore } from '../../store';
import QueueInfoPanel from './QueueInfoPanel';
import TaskPriorityPanel from './TaskPriorityPanel';
import WaitingRoomPanel from './WaitingRoomPanel';
import UnifiedPatientTable from './UnifiedPatientTable';

const { Text } = Typography;

const UnifiedDashboard = () => {
  const {
    unifiedRecords,
    queueInfo,
    isFetching,
    fetchError,
    lastFetchedAt,
    fetchUnifiedData,
    getSummary,
  } = useUnifiedDashboardStore();

  // Main tab state: 'overview' or 'dashboard'
  const [activeMainTab, setActiveMainTab] = useState('overview');

  const isInitialLoad = !lastFetchedAt;

  // Get summary statistics - recompute when records change
  const summary = useMemo(
    () => getSummary(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unifiedRecords, queueInfo]
  );

  // Format last fetched time
  const lastFetchedDisplay = useMemo(() => {
    if (!lastFetchedAt) return 'Never';
    const date = new Date(lastFetchedAt);
    return date.toLocaleTimeString();
  }, [lastFetchedAt]);

  // Main tab items: Overview and Dashboard
  const mainTabItems = [
    {
      key: 'overview',
      label: (
        <Space>
          <AppstoreOutlined />
          Overview
        </Space>
      ),
      children:
        isFetching && isInitialLoad ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '60px 0',
            }}
          >
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
              tip="Loading dashboard data..."
            />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Waiting Room Section */}
            <Card
              size="default"
              title={
                <Space>
                  <ClockCircleOutlined />
                  Waiting Room
                  {summary.waiting > 0 && (
                    <Badge
                      count={summary.waiting}
                      size="small"
                      style={{ backgroundColor: '#52c41a' }}
                    />
                  )}
                </Space>
              }
              styles={{ body: { padding: 0 } }}
            >
              <WaitingRoomPanel />
            </Card>

            {/* Queue Info + Task Priority side by side */}
            <div
              style={{
                display: 'flex',
                gap: 16,
                minHeight: 'calc(100vh - 300px)',
              }}
            >
              {/* Queue Info - wider, determines row height */}
              <Card
                size="default"
                title={
                  <Space>
                    <TeamOutlined />
                    Queue Info
                  </Space>
                }
                styles={{ body: { padding: 0 } }}
                style={{ flex: 5, minWidth: 0 }}
              >
                <QueueInfoPanel />
              </Card>

              {/* Task Priority - matches Queue Info height, scrolls internally */}
              <div style={{ flex: 2, minWidth: 300, position: 'relative' }}>
                <Card
                  size="default"
                  title={
                    <Space>
                      <ArrowUpOutlined />
                      DR Task Priority
                    </Space>
                  }
                  styles={{
                    body: {
                      padding: 0,
                      overflow: 'auto',
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    },
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <TaskPriorityPanel />
                </Card>
              </div>
            </div>
          </div>
        ),
    },
    {
      key: 'dashboard',
      label: (
        <Space>
          <DashboardOutlined />
          Dashboard
        </Space>
      ),
      children: (
        <>
          <UnifiedPatientTable />

          {/* Footer Stats */}
          <div
            style={{
              marginTop: 16,
              padding: '8px 16px',
              backgroundColor: '#fafafa',
              borderRadius: 4,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text type="secondary">
              {summary.activeCalls} active calls | {summary.slaBreaches}{' '}
              waiting-room SLA breaches
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Auto-refresh: 60s
            </Text>
          </div>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px' }}>
      {/* Header with stats and controls */}
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        styles={{ body: { padding: '12px 16px' } }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          {/* Summary Stats */}
          <Space size="large" wrap>
            <Tooltip title="Active calls in progress">
              <Space>
                <Badge status="processing" />
                <Text strong>{summary.activeCalls}</Text>
                <Text type="secondary">Active Calls</Text>
              </Space>
            </Tooltip>

            <Tooltip title="Patients currently in the waiting room">
              <Space>
                <ClockCircleOutlined style={{ color: '#52c41a' }} />
                <Text strong>{summary.waiting}</Text>
                <Text type="secondary">Waiting</Text>
              </Space>
            </Tooltip>

            <Tooltip title="Patients currently with a doctor or provider">
              <Space>
                <Badge status="success" />
                <Text strong>{summary.withDoctor}</Text>
                <Text type="secondary">With Doctor</Text>
              </Space>
            </Tooltip>

            {summary.slaBreaches > 0 && (
              <Tooltip title="Waiting-room patients whose wait time is at or above their configured SLA">
                <Space>
                  <WarningOutlined style={{ color: '#ff4d4f' }} />
                  <Text strong style={{ color: '#ff4d4f' }}>
                    {summary.slaBreaches}
                  </Text>
                  <Text type="secondary">SLA Breaches</Text>
                </Space>
              </Tooltip>
            )}
          </Space>

          {/* Controls */}
          <Space>
            {isFetching && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                <SyncOutlined spin style={{ marginRight: 4 }} />
                Refreshing...
              </Text>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>
              Last updated: {lastFetchedDisplay}
            </Text>

            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchUnifiedData()}
              disabled={isFetching}
            >
              Refresh
            </Button>
          </Space>
        </div>

        {/* Error display */}
        {fetchError && (
          <div style={{ marginTop: 8 }}>
            <Text type="danger">Error: {fetchError}</Text>
          </div>
        )}
      </Card>

      {/* Main Tabs: Overview / Dashboard */}
      <Card size="small" styles={{ body: { padding: '0 16px 16px' } }}>
        <Tabs
          activeKey={activeMainTab}
          onChange={setActiveMainTab}
          items={mainTabItems}
          tabBarStyle={{ marginBottom: 16 }}
        />
      </Card>
    </div>
  );
};

export default UnifiedDashboard;
